import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { bffMfaApi } from './api/bffMfaApi'
import { getBffAuthErrorMessage } from '../bffAuthErrors'
import { useAsyncRunner } from '@/common/hooks/useAsyncRunner'
import { API_ROUTES } from '@/common/constants'
import type { User } from '@/common/types'

export type MfaRegistrationStep = 'associate' | 'verify'

interface UseBffMfaRegistrationResult {
    step: MfaRegistrationStep
    secretCode: string | null
    isRunning: boolean
    startAssociation: () => Promise<void>
    verifyCode: (code: string) => Promise<void>
}

export function useBffMfaRegistration(onEnabled: () => void): UseBffMfaRegistrationResult {
    const [step, setStep] = useState<MfaRegistrationStep>('associate')
    const [secretCode, setSecretCode] = useState<string | null>(null)
    const { isRunning, run } = useAsyncRunner()
    const queryClient = useQueryClient()

    const startAssociation = useCallback(
        () =>
            run(async () => {
                try {
                    const { secretCode: code } = await bffMfaApi.associate()
                    setSecretCode(code)
                    setStep('verify')
                } catch (error: unknown) {
                    toast.error(getBffAuthErrorMessage(error))
                }
            }),
        [run],
    )

    const verifyCode = useCallback(
        (code: string) =>
            run(async () => {
                try {
                    const { mfaEnabled } = await bffMfaApi.verify({ code })
                    if (!mfaEnabled) {
                        throw new Error('Two-factor authentication could not be enabled.')
                    }
                    queryClient.setQueryData([API_ROUTES.user], (old: User | undefined) =>
                        old ? { ...old, mfaEnabled } : old,
                    )
                    onEnabled()
                } catch (error: unknown) {
                    toast.error(getBffAuthErrorMessage(error))
                }
            }),
        [onEnabled, queryClient, run],
    )

    return { step, secretCode, isRunning, startAssociation, verifyCode }
}
